// M2 (Gusher & Rotary, 1901-1945) region content: new regions, extra risk keyframes for 1901-1945,
// closures (regions shut to foreign operators), royalty schedules and local "protection" partners.
// Merged into REGIONS by src/data/regions.js. Pure data + pure helpers; no imports from regions.js
// (avoids an import cycle).
//
// M1 safety rule (enforced by mergeRiskKeyframes and tests/m2data.test.js): an extra keyframe may only be
// inserted AFTER a region's first existing keyframe later than 1900, so regionRisk(id, year <= 1900) is
// bit-for-bit unchanged and the Pioneer-era balance is untouched.

const r = (year, stability, security, expropriation, corruption) => ({ year, stability, security, expropriation, corruption });

// ------------------------------------------------------------------ new regions
export const M2_REGIONS = {
  bahrain: {
    id: 'bahrain', name: 'Bahrain (Awali)', country: 'Bahrain (British protectorate)', continent: 'asia', currency: 'USD',
    lat: 26.03, lon: 50.55, opensYear: 1930, leaseMult: 0.3, royalty: 0.12, transportToMarket: 1.0,
    hazards: ['sabotage'],
    risk: [r(1930, 0.85, 0.85, 0.02, 0.4), r(1940.8, 0.7, 0.6, 0.02, 0.4), // Italian bombers hit the Awali refinery, Oct 1940
           r(1941.5, 0.85, 0.85, 0.02, 0.4), r(1971, 0.8, 0.8, 0.3, 0.4), r(1980, 0.8, 0.8, 0.6, 0.45), // BAPCO taken over 1974-80
           r(2011, 0.4, 0.5, 0.1, 0.5), r(2025, 0.7, 0.75, 0.05, 0.4)],
    blurb: 'A small island, a single dome, and the first oil on the Arab side of the Gulf. Socal holds the concession through BAPCO.',
  },
  alberta: {
    id: 'alberta', name: 'Alberta, Canada', country: 'Canada', continent: 'north_america', currency: 'USD',
    lat: 50.67, lon: -114.28, opensYear: 1914, leaseMult: 0.6, royalty: 0.125, transportToMarket: 0.6,
    hazards: ['theft', 'cold'],
    risk: [r(1914, 0.95, 0.95, 0, 0.15), r(2025, 0.97, 0.99, 0, 0.05)],
    blurb: 'Foothills west of Calgary. Sour wet gas, flare pits lighting up the night, and an oil leg nobody has found yet.',
  },
};

// ------------------------------------------------------------------ extra keyframes (1901-1945 detail)
// Fractional years mark the month: 1916.85 is about early November 1916.
export const M2_RISK_KEYFRAMES = {
  texas: [
    r(1901.1, 0.75, 0.65, 0, 0.4),   // Spindletop boom: Beaumont's "South Africa" district, claim jumpers
    r(1903, 0.85, 0.8, 0, 0.35),
    r(1918.5, 0.8, 0.7, 0, 0.4),     // Ranger & Burkburnett boomtowns
    r(1921, 0.85, 0.75, 0, 0.4),
    r(1930.9, 0.75, 0.7, 0, 0.45),   // East Texas: hot oil, hijackers, tent cities
    r(1934, 0.85, 0.8, 0, 0.35),
    r(1942, 0.9, 0.9, 0, 0.3),
  ],
  oklahoma: [
    r(1912.5, 0.8, 0.7, 0, 0.45),    // Cushing boom
    r(1915, 0.75, 0.65, 0, 0.5),     // Drumright & Oilton: lawless boomtowns
    r(1921, 0.7, 0.6, 0, 0.55),      // violent years in the oil towns
    r(1926.8, 0.75, 0.65, 0, 0.5),   // Seminole: Bowlegs and "Little Juarez"
    r(1931.6, 0.6, 0.7, 0, 0.45),    // Governor Murray puts the oil fields under martial law
    r(1935, 0.85, 0.85, 0, 0.35),
  ],
  california: [
    r(1921.5, 0.9, 0.82, 0, 0.35),   // Signal Hill town-lot frenzy and stock swindles
    r(1926, 0.92, 0.88, 0, 0.3),
  ],
  mexico: [
    r(1908, 0.65, 0.6, 0.02, 0.6),
    r(1913.1, 0.15, 0.1, 0.05, 0.7),  // Decena Trágica: Madero murdered
    r(1914.3, 0.1, 0.1, 0.08, 0.75),  // Tampico affair, US occupies Veracruz; Peláez's private army in the Huasteca
    r(1917.1, 0.25, 0.2, 0.25, 0.75), // Constitution Article 27: the subsoil belongs to the nation
    r(1918, 0.3, 0.25, 0.2, 0.7),
    r(1923, 0.5, 0.45, 0.12, 0.7),    // Bucareli agreements
    r(1927, 0.45, 0.45, 0.2, 0.7),    // Calles petroleum law
    r(1934.9, 0.5, 0.5, 0.25, 0.65),  // Cárdenas takes office
    r(1936, 0.45, 0.5, 0.3, 0.65),    // oil workers' union, wage arbitration
    r(1938.3, 0.5, 0.55, 0.99, 0.65), // 18 Mar 1938 expropriation (closure handles the aftermath)
  ],
  venezuela: [
    r(1922.9, 0.6, 0.55, 0.02, 0.85), // Gómez concessions boom after Barroso II
    r(1929, 0.6, 0.6, 0.03, 0.85),
    r(1935.95, 0.35, 0.4, 0.05, 0.75), // Gómez dies; riots in Maracaibo
    r(1936.5, 0.5, 0.5, 0.06, 0.7),
    r(1943.2, 0.55, 0.6, 0.08, 0.65),  // Hydrocarbons Law: royalty to 1/6
  ],
  persia: [
    r(1906, 0.35, 0.35, 0.03, 0.7),   // Constitutional Revolution
    r(1909, 0.3, 0.3, 0.03, 0.7),     // Bakhtiari ride on Tehran
    r(1915.1, 0.15, 0.15, 0.05, 0.75), // WWI: tribesmen cut the Abadan pipeline, German agents in the south
    r(1918.9, 0.3, 0.3, 0.05, 0.7),
    r(1921.15, 0.45, 0.4, 0.05, 0.7), // Reza Khan's coup
    r(1926, 0.6, 0.55, 0.05, 0.65),
    r(1932.9, 0.5, 0.55, 0.45, 0.65), // Reza Shah cancels the D'Arcy concession (Nov 1932)
    r(1933.4, 0.65, 0.6, 0.08, 0.6),  // new 60-year concession
    r(1941.65, 0.25, 0.35, 0.1, 0.65), // Anglo-Soviet invasion
    r(1942.5, 0.4, 0.5, 0.08, 0.65),
    r(1946, 0.35, 0.45, 0.1, 0.7),    // Azerbaijan crisis
  ],
  iraq: [
    r(1932.8, 0.55, 0.5, 0.05, 0.6),  // independence, IPC concession continues
    r(1936.8, 0.35, 0.4, 0.06, 0.65), // Bakr Sidqi coup
    r(1941.3, 0.15, 0.2, 0.2, 0.7),   // Rashid Ali coup; Anglo-Iraqi War, Kirkuk threatened
    r(1941.5, 0.4, 0.4, 0.08, 0.65),
    r(1948, 0.4, 0.45, 0.08, 0.65),
  ],
  saudi: [
    r(1938.2, 0.8, 0.75, 0.02, 0.5),
    r(1940.8, 0.7, 0.6, 0.02, 0.5),   // Italian air raid on Dhahran, 19 Oct 1940
    r(1941.5, 0.8, 0.75, 0.02, 0.5),
  ],
  romania: [
    r(1913, 0.6, 0.6, 0.02, 0.6),
    r(1916.85, 0.1, 0.05, 0.3, 0.6),  // British sabotage team wrecks the Prahova fields ahead of the Germans
    r(1917.5, 0.25, 0.3, 0.3, 0.6),   // German occupation; Allied-owned property requisitioned
    r(1918.9, 0.4, 0.4, 0.15, 0.6),
    r(1937, 0.45, 0.5, 0.06, 0.65),
    r(1940.5, 0.25, 0.3, 0.25, 0.7),  // Iron Guard, Axis alignment
    r(1941.5, 0.2, 0.25, 0.4, 0.7),   // at war with the Allies
    r(1943.58, 0.1, 0.05, 0.4, 0.7),  // Operation Tidal Wave, 1 Aug 1943
    r(1944.7, 0.05, 0.05, 0.8, 0.7),  // Soviet occupation of Ploiești
  ],
  baku: [
    r(1914.6, 0.35, 0.3, 0.1, 0.55),
    r(1917.9, 0.15, 0.15, 0.3, 0.5),  // revolution
    r(1918.25, 0.05, 0.05, 0.4, 0.5), // March Days
    r(1918.7, 0.05, 0.05, 0.5, 0.5),  // Battle of Baku
    r(1919.5, 0.3, 0.3, 0.4, 0.5),
    r(1920.4, 0.1, 0.3, 0.99, 0.5),   // Red Army; the oil industry nationalized
    r(1942.6, 0.1, 0.15, 0.99, 0.5),  // German drive on the Caucasus; demolition teams ready
    r(1943.2, 0.3, 0.4, 0.99, 0.5),
    r(1946, 0.4, 0.5, 0.99, 0.5),
    r(1990, 0.2, 0.3, 0.9, 0.7),
  ],
  sumatra: [
    r(1941.9, 0.3, 0.3, 0.1, 0.5),
    r(1942.1, 0.05, 0.02, 0.95, 0.5), // Japanese invasion, scorched earth at Palembang and Pangkalan Brandan
    r(1945.7, 0.2, 0.2, 0.5, 0.6),
  ],
  galicia: [
    r(1915.5, 0.3, 0.25, 0.1, 0.5),   // Russian occupation of Boryslav
    r(1925, 0.65, 0.6, 0.04, 0.5),
  ],
};

// ------------------------------------------------------------------ closures
// While closed, foreigners cannot lease, spud or buy in the region; anything they still hold on `from`
// is seized (compensation fraction paid out). Years are fractional.
export const REGION_CLOSURES = {
  baku:     [{ from: 1920.33, until: 1994.72, compensation: 0.0,  reason: 'Bolshevik nationalization of the Baku oil industry' }],
  mexico:   [{ from: 1938.21, until: 2014.0,  compensation: 0.05, reason: 'Cárdenas expropriation; PEMEX holds the monopoly' }],
  romania:  [{ from: 1941.5,  until: 1945.0,  compensation: 0.0,  reason: 'Romania at war with the Allies: Allied property sequestered' },
             { from: 1948.45, until: 1990.0,  compensation: 0.0,  reason: 'Communist nationalization (M3)' }],
  galicia:  [{ from: 1939.7,  until: 1991.7,  compensation: 0.0,  reason: 'Soviet annexation of Eastern Galicia' }],
  sumatra:  [{ from: 1942.1,  until: 1945.7,  compensation: 0.0,  reason: 'Japanese occupation of the Dutch East Indies' }],
};

// Royalty (state/landowner share) changes by year, overriding region/field royalty from that year on.
export const ROYALTY_SCHEDULE = {
  venezuela: [[1914, 0.10], [1922, 0.11], [1943.2, 0.1667]],
  saudi:     [[1933, 0.12], [1950.95, 0.5]],  // 30 Dec 1950 fifty-fifty agreement with Aramco
  kuwait:    [[1938, 0.12], [1951.95, 0.5]],  // KOC fifty-fifty, Dec 1951
  persia:    [[1901, 0.16], [1933.4, 0.2]],
  iraq:      [[1927, 0.15]],
  mexico:    [[1901, 0.10]],                  // landowner royalty; the 1912/1917/1921 production & export taxes are rules.SEVERANCE.mexico
};

// Local "protection" partners (DESIGN §12.4 local partnerships), M2 scope.
//   revenueShare: fraction of field revenue paid; attackMult / expropriationMult scale those hazards.
export const LOCAL_PARTNERS = {
  mexico:    [{ id: 'pelaez', name: "General Peláez's Huasteca army", from: 1914.8, until: 1920.4, revenueShare: 0.08, attackMult: 0.35, expropriationMult: 1.0,
                desc: 'Pay the general and his men keep Carrancistas and bandits out of the camps.' }],
  persia:    [{ id: 'bakhtiari_khans', name: 'Bakhtiari khans', from: 1905, until: 1933, revenueShare: 0.03, attackMult: 0.5, expropriationMult: 0.9,
                desc: 'D’Arcy’s men gave the khans three percent and a guard contract. The tribesmen leave the wells alone.' }],
  iraq:      [{ id: 'tribal_levies', name: 'Tribal guard levies', from: 1927, until: 1958, revenueShare: 0.04, attackMult: 0.6, expropriationMult: 0.9,
                desc: 'Local sheikhs supply guards along the pipeline route.' }],
  venezuela: [{ id: 'gomez_circle', name: "Friends of General Gómez", from: 1914, until: 1935.95, revenueShare: 0.05, attackMult: 0.8, expropriationMult: 0.3,
                desc: 'Concessions flow through the dictator’s cronies. So does a slice of the revenue.' }],
};

// ------------------------------------------------------------------ helpers (pure)
// Insert extra keyframes into a sorted base list. Returns a NEW array. A keyframe at year p only changes
// interpolated values strictly between its neighbours, so it is M1-safe iff the nearest base keyframe
// before it is at year >= 1900 (values for years <= 1900 then stay identical). Throws otherwise, or on a
// duplicate year.
export function mergeRiskKeyframes(base, extra, regionId = '?') {
  if (!extra?.length) return base.slice();
  const years = new Set(base.map((k) => k.year));
  for (const k of extra) {
    const prev = base.filter((b) => b.year < k.year).reduce((m, b) => Math.max(m, b.year), -Infinity);
    if (!(prev >= 1900)) throw new Error(`${regionId}: M2 keyframe ${k.year} would change pre-1901 risk (previous base keyframe ${prev})`);
    if (years.has(k.year)) throw new Error(`${regionId}: duplicate keyframe year ${k.year}`);
    years.add(k.year);
  }
  return [...base, ...extra].sort((a, b) => a.year - b.year);
}

// Closure active for region at fractional year (or null).
export function regionClosure(regionId, year) {
  return (REGION_CLOSURES[regionId] ?? []).find((c) => year >= c.from && year < c.until) ?? null;
}

// Scheduled royalty for region at fractional year, or null when no schedule applies (use field/region royalty).
export function scheduledRoyalty(regionId, year) {
  const s = ROYALTY_SCHEDULE[regionId];
  if (!s || year < s[0][0]) return null;
  let v = s[0][1];
  for (const [y, x] of s) if (year >= y) v = x;
  return v;
}

export function partnersAvailable(regionId, year) {
  return (LOCAL_PARTNERS[regionId] ?? []).filter((p) => year >= p.from && year < p.until);
}
